// Команды (cmdk): shadcn 4.21.1 (base-mira), подогнаны под ui-spec §4–§5 (задача D5):
// - ⌘K открывают десятки раз в день с клавиатуры — окно палитры НЕ анимируется (ui-spec §5, Эмиль: Raycast без
//   анимации). Поэтому `CommandDialog` собран прямо на Base UI Dialog, без классов .dialog и .scrim;
// - поле во всю ширину с иконкой поиска, без рамки и кольца: в окне оно одно, и фокус в нём всегда;
// - пункты `h-8` (на телефоне `h-10`), текст 13 px, выбранный — фон --muted без перехода; группа — подпись 12/16;
// - лишняя галочка у пункта и полупрозрачные цвета убраны.
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from 'cn';
import { SearchIcon } from 'lucide-react';
import type * as React from 'react';

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn('flex size-full flex-col overflow-hidden bg-popover text-popover-foreground', className)}
      {...props}
    />
  );
}

interface CommandDialogProps extends Omit<DialogPrimitive.Root.Props, 'children'> {
  /** Заголовок окна для чтения с экрана (на экране его нет). */
  readonly title: string;
  readonly className?: string;
  readonly children: React.ReactNode;
}

/** Окно палитры: сверху по центру, без анимации появления и ухода. */
function CommandDialog({ title, children, className, ...props }: CommandDialogProps) {
  return (
    <DialogPrimitive.Root {...props}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop data-slot="command-dialog-overlay" className="fixed inset-0 z-50 min-h-dvh bg-scrim" />
        <DialogPrimitive.Popup
          data-slot="command-dialog"
          data-hotkeys="off"
          className={cn(
            'fixed top-[12vh] left-1/2 z-50 flex max-h-[min(32rem,76dvh)] w-[calc(100%-1rem)] max-w-140 -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-dialog outline-none max-sm:top-2',
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {/* Ctrl+N/J/P/K cmdk («vim») выключены: Ctrl K в пульте открывает и закрывает саму палитру. */}
          <Command label={title} vimBindings={false} className="min-h-0 flex-1">
            {children}
          </Command>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function CommandInput({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div data-slot="command-input-wrapper" className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3 max-sm:h-12">
      <SearchIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('min-h-0 flex-1 scroll-py-1 overflow-x-hidden overflow-y-auto overscroll-contain p-1', className)}
      {...props}
    />
  );
}

function CommandEmpty({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn('py-8 text-center text-base text-muted-foreground', className)}
      {...props}
    />
  );
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'overflow-hidden py-1 text-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:pt-1 **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:text-sm **:[[cmdk-group-heading]]:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator data-slot="command-separator" className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'relative flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-base outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-selected:bg-muted max-sm:h-10 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4 [&_svg:not([class*=text-])]:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return <span data-slot="command-shortcut" className={cn('ml-auto text-sm text-muted-foreground', className)} {...props} />;
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
